import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Share2, Plus, Trash2, X, KeyRound, User, Mail, Copy } from "lucide-react";
import { api } from "../lib/api";

const EMPTY_INVITE = { email: "", name: "", password: "", role: "family" };
// Tiers (see lib/permissions.js for what each one sees)
const ROLE_OPTIONS = [
  ["family", "Family", "Parents · outputs, analytics, film, IQ, training, recruiting."],
  ["player", "Player", "The player's own login · outputs only, read-only."],
  ["trainer", "Trainer", "Read-only + Vibes · edits the Workout Log."],
  ["agent", "Agent / Coach", "Agent or team coach · read-only, includes Vibes."],
  ["lite", "Lite", "Minimal dashboard · bio, review, archetype, role, film, IQ, routines."],
];

// Simple readable random password so admins can share it manually.
function makePassword() {
  const w = ["Court", "Zone", "Rise", "Guard", "Ball", "Hoops", "Post", "Wing", "Coach", "Drive"];
  const pick = w[Math.floor(Math.random() * w.length)];
  const num = Math.floor(1000 + Math.random() * 9000);
  return `${pick}${num}!`;
}

export default function ShareAccessDialog({ playerId, playerName, open, onClose }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(EMPTY_INVITE);
  const [inviting, setInviting] = useState(false);
  const [lastCreated, setLastCreated] = useState(null); // {email, password} for the copy-to-clipboard confirmation

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.listPlayerAccess(playerId);
      setGrants(list || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    load();
    setInvite({ ...EMPTY_INVITE, password: makePassword() });
    setLastCreated(null);
  }, [open, playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const submit = async () => {
    if (!invite.email.trim()) { toast.error("Email is required"); return; }
    if (invite.password.length < 6) { toast.error("Password must be 6+ chars"); return; }
    setInviting(true);
    try {
      const res = await api.grantPlayerAccess(playerId, {
        email: invite.email.trim(),
        password: invite.password,
        name: invite.name.trim() || null,
        role: invite.role || "family",
      });
      if (res.created) {
        toast.success("Access granted", { description: `New ${invite.role} account for ${invite.email}` });
        setLastCreated({ email: invite.email, password: invite.password, role: invite.role });
      } else {
        toast.success("Access granted", { description: `${invite.email} already had an account` });
        setLastCreated(null);
      }
      setInvite({ ...EMPTY_INVITE, password: makePassword() });
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setInviting(false); }
  };

  const revoke = async (g) => {
    if (!window.confirm(`Remove access for ${g.email}?`)) return;
    try {
      await api.revokePlayerAccess(playerId, g.grant_id);
      toast.success("Access removed");
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const copyCreds = () => {
    if (!lastCreated) return;
    const text = `Sign in at your Maximize Analytics link:\nEmail: ${lastCreated.email}\nPassword: ${lastCreated.password}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Credentials copied to clipboard"),
      () => toast.error("Copy failed — please select the text manually"),
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="share-access-dialog">
      <div className="bg-[#141414] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-[var(--org-accent)]" />
            <div>
              <h3 className="font-heading text-xl uppercase tracking-[0.15em] text-white">Share Player Page</h3>
              <div className="text-xs text-[#a0a0a0] mt-0.5">Grant view-only access for {playerName}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0a0a0] hover:text-white" data-testid="share-access-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* New invite form */}
        <div className="px-6 py-5 border-b border-white/10 bg-[#151515]">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0] mb-3">Create Access</div>
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Email" icon={<Mail className="w-3 h-3" />}>
              <input
                type="email" value={invite.email}
                onChange={(e) => setInvite(v => ({ ...v, email: e.target.value }))}
                placeholder="parent@example.com"
                className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                data-testid="share-access-email"
              />
            </Labeled>
            <Labeled label="Name (optional)" icon={<User className="w-3 h-3" />}>
              <input
                value={invite.name}
                onChange={(e) => setInvite(v => ({ ...v, name: e.target.value }))}
                placeholder="Parent / Player name"
                className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                data-testid="share-access-name"
              />
            </Labeled>
            <Labeled label="Access Level" icon={<Share2 className="w-3 h-3" />} span={2}>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.map(([role, label, hint]) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setInvite(v => ({ ...v, role }))}
                    className={`px-3 py-2 border text-left ${invite.role === role ? "border-[var(--org-accent)] bg-[var(--org-accent)]/10" : "border-white/10 bg-[#101010] hover:border-white/30"}`}
                    data-testid={`share-access-role-${role}`}
                  >
                    <div className={`text-[11px] uppercase tracking-[0.25em] font-bold ${invite.role === role ? "text-[var(--org-accent)]" : "text-white"}`}>{label}</div>
                    <div className="text-[10px] text-[#a0a0a0] mt-0.5">{hint}</div>
                  </button>
                ))}
              </div>
            </Labeled>
            <Labeled label="Temporary Password" icon={<KeyRound className="w-3 h-3" />} span={2}>
              <div className="flex gap-2">
                <input
                  value={invite.password}
                  onChange={(e) => setInvite(v => ({ ...v, password: e.target.value }))}
                  className="flex-1 bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[var(--org-accent)]"
                  data-testid="share-access-password"
                />
                <button
                  type="button"
                  onClick={() => setInvite(v => ({ ...v, password: makePassword() }))}
                  className="px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white hover:border-[var(--org-accent)]"
                  data-testid="share-access-regen"
                >
                  Regenerate
                </button>
              </div>
              <div className="mt-1 text-[10px] text-[#757575]">Share this manually with the parent/player after you save.</div>
            </Labeled>
          </div>
          <div className="flex items-center justify-end mt-4">
            <button
              onClick={submit} disabled={inviting}
              className="flex items-center gap-2 px-5 py-2.5 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              data-testid="share-access-submit"
            >
              {inviting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Grant Access
            </button>
          </div>
          {lastCreated && (
            <div className="mt-4 p-3 border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5" data-testid="share-access-created">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] mb-2">Account Created — Send These Details</div>
              <div className="font-mono text-xs text-white space-y-1">
                <div>Email: {lastCreated.email}</div>
                <div>Password: {lastCreated.password}</div>
              </div>
              <button
                onClick={copyCreds}
                className="mt-3 flex items-center gap-2 px-3 py-1.5 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)]"
                data-testid="share-access-copy"
              >
                <Copy className="w-3 h-3" /> Copy Sign-in Info
              </button>
            </div>
          )}
        </div>

        {/* Existing grants */}
        <div className="px-6 py-5">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0] mb-3">
            People with Access · {grants.length}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-[#a0a0a0]">
              <Loader2 className="w-4 h-4 animate-spin" /> loading…
            </div>
          ) : grants.length === 0 ? (
            <div className="text-center py-8 text-[#757575] text-sm">
              No one else has access yet.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="share-access-list">
              {grants.map(g => (
                <li key={g.grant_id} className="flex items-center justify-between p-3 border border-white/10 bg-[#101010]" data-testid={`share-access-row-${g.grant_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm text-white truncate">{g.name || g.email}</div>
                      <span
                        className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 border font-bold"
                        style={g.role === "trainer"
                          ? { color: "var(--org-accent)", borderColor: "var(--org-accent)66", background: "var(--org-accent)15" }
                          : g.role === "player" ? { color: "#4ade80", borderColor: "#4ade8066", background: "#4ade8010" }
                          : g.role === "agent" ? { color: "#f0a35b", borderColor: "#f0a35b66", background: "#f0a35b10" }
                          : g.role === "lite" ? { color: "#9ca3af", borderColor: "#9ca3af66", background: "#9ca3af10" }
                          : { color: "#a0a0a0", borderColor: "#a0a0a044", background: "#a0a0a010" }}
                      >
                        {g.role === "agent" ? "Agent / Coach" : (g.role || "family")}
                      </span>
                    </div>
                    {g.name && <div className="text-xs text-[#757575] truncate mt-0.5">{g.email}</div>}
                  </div>
                  <button
                    onClick={() => revoke(g)}
                    className="p-2 text-[#a0a0a0] hover:text-red-300"
                    title="Remove access"
                    data-testid={`share-access-revoke-${g.grant_id}`}
                  ><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, icon, span = 1, children }) {
  const cls = span === 2 ? "col-span-2" : "";
  return (
    <div className={cls}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#757575] mb-1">
        {icon}{label}
      </div>
      {children}
    </div>
  );
}
